import { Component, Input, Inject, NgZone, OnChanges, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { ChartLoadingComponent } from '../chart-loading/chart-loading.component';
import { HEX_COLOR_PALETTE } from '../utils/color-palette';
import { ChartValueType, valueFormatter } from '../utils/chart-utils';

export interface ChartDonutLegendConfig {
  fontSize?: number;
  fontWeight?: '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | 'normal' | 'bold' | 'bolder' | 'lighter';
}

export interface ChartDonutField {
  field: string;
  name: string;
}

export interface ChartDonutConfig {
  categoryField?: string;                        // dinámico: cada fila del data = un slice
  valueField?: string;
  fields?: ChartDonutField[];                    // estático: varias columnas de UNA fila = varios slices — se omite si se usa categoryField/valueField
  type?: ChartValueType;                         // formato del tooltip
  legend?: ChartDonutLegendConfig;
  title?: string;
}

/** Esto es para armar un card, no hace ningun efecto en el componente */
export interface ChartDonutCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartDonutConfig;
}

@Component({
  selector: 'app-chart-donut',
  standalone: true,
  imports: [CommonModule, ChartLoadingComponent],
  styles: `
    :host {
      display: block;
    }

    .donut-canvas {
      width: 100%;
      height: 245px;
      transition: height 0.2s ease;
    }
  `,
  template: `
    <app-chart-loading *ngIf="isLoading; else canvas"></app-chart-loading>
    <ng-template #canvas>
      <div [id]="chartId" class="donut-canvas"></div>
    </ng-template>
  `
})
export class ChartDonutComponent implements OnChanges, OnDestroy {

  chartId!: string;
  @Input() config!: ChartDonutConfig;
  @Input() data: any[] = [];

  isLoading = true;

  private root?: am5.Root;
  private series?: am5percent.PieSeries;
  private centerLabelObj?: am5.Label;
  private legend?: am5.Legend;
  private resizeObserver?: ResizeObserver;
  private resolvedSlices: any[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.chartId = `chart-donut-${crypto.randomUUID()}`;
  }

  ngOnChanges(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config || !this.data?.length) return;

    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    this.resolvedSlices = this.resolveSlices();
    this.series?.data.setAll(this.resolvedSlices);
    this.legend?.data.setAll(this.series!.dataItems);
    this.centerLabelObj?.set('text', this.buildCenterText());
  }

  private resolveSlices(): any[] {
    const { fields, categoryField, valueField, type } = this.config;

    if (fields) {
      const row = this.data[0];
      return fields.map((f, index) => ({
        label: f.name,
        value: row[f.field],
        formattedValue: valueFormatter(type, row[f.field]),
        settings: { fill: am5.color(HEX_COLOR_PALETTE[index % HEX_COLOR_PALETTE.length]) },
      }));
    }

    return this.data.map((row, index) => ({
      label: row[categoryField!],
      value: row[valueField!],
      formattedValue: valueFormatter(type, row[valueField!]),
      settings: { fill: am5.color(HEX_COLOR_PALETTE[index % HEX_COLOR_PALETTE.length]) },
    }));
  }

  private initChart(): void {
    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      this.root = root;

      const textColor  = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);

      const wrapper = root.container.children.push(
        am5.Container.new(root, {
          width: am5.percent(100),
          height: am5.percent(100),
          layout: root.horizontalLayout,
        })
      );

      const chart = wrapper.children.push(
        am5percent.PieChart.new(root, {
          innerRadius: am5.percent(60),
          paddingTop: 6,
          paddingBottom: 6,
        })
      );

      const series = chart.series.push(
        am5percent.PieSeries.new(root, {
          valueField: 'value',
          categoryField: 'label',
          alignLabels: false,
        })
      );
      this.series = series;

      series.slices.template.setAll({
        templateField: 'settings',
        tooltipText: '{label}: {formattedValue}',
        strokeOpacity: 0,
        cornerRadius: 2,
        shadowOpacity: 0.1,
        shadowOffsetX: 2,
        shadowOffsetY: 2,
        shadowColor: am5.color(0x000000),
        shadowBlur: 10,
      });
      series.slices.template.set('fillGradient', am5.RadialGradient.new(root, {
        stops: [
          { brighten: 0 },
          { brighten: -0.5 },
          { brighten: -0.3 },
          { brighten: 0.5 },
          { brighten: -0.3 },
        ],
      }));
      series.labels.template.setAll({ forceHidden: true });
      series.ticks.template.setAll({ forceHidden: true });

      this.centerLabelObj = chart.seriesContainer.children.push(
        am5.Label.new(root, {
          text: this.buildCenterText(),
          textAlign: 'center',
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          fill: textColor,
          populateText: true,
        })
      );

      const legend = wrapper.children.push(
        am5.Legend.new(root, {
          centerY: am5.percent(50),
          y: am5.percent(50),
          layout: root.verticalLayout,
          marginLeft: 16,
          height: am5.percent(100),
          verticalScrollbar: am5.Scrollbar.new(root, { orientation: 'vertical' }),
        })
      );
      this.legend = legend;
      const legendFontSize   = this.config.legend?.fontSize   ?? 12;
      const legendFontWeight = this.config.legend?.fontWeight ?? '700';
      legend.labels.template.setAll({ fill: textColor, fontSize: legendFontSize, fontWeight: legendFontWeight });
      legend.valueLabels.template.setAll({ fill: mutedColor, fontSize: legendFontSize, fontWeight: legendFontWeight });

      // Responsive: move legend below on narrow screens
      const el = document.getElementById(this.chartId);
      if (el) {
        this.resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect.width ?? 0;
          if (width === 0) return;
          if (width <= 500) {
            el.style.height = '380px';
            wrapper.set('layout', root.verticalLayout);
            chart.setAll({ height: am5.percent(65) });
            legend.setAll({
              x: am5.percent(50), centerX: am5.percent(50),
              y: undefined, centerY: undefined,
              height: am5.percent(35),
              marginLeft: 0, layout: root.gridLayout,
            });
          } else {
            el.style.height = '245px';
            wrapper.set('layout', root.horizontalLayout);
            chart.setAll({ height: undefined });
            legend.setAll({
              x: undefined, centerX: undefined,
              y: am5.percent(50), centerY: am5.percent(50),
              height: am5.percent(100),
              marginLeft: 16, layout: root.verticalLayout,
            });
          }
        });
        this.resizeObserver.observe(el);
      }

      series.appear(500, 100);
    });
  }

  private buildCenterText(): string {
    const total = this.resolvedSlices.reduce((sum, s) => sum + (s.value ?? 0), 0);
    const centerLabel = valueFormatter(this.config.type, total);
    return `[fontSize:20px fontWeight:800]${centerLabel}[/]\n[fontSize:12px]Total[/]`;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.root?.dispose();
  }
}
